import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import api from '../utils/api';
import '../styles/PremiumDashboard.css';

function formatPhoneForWhatsApp(telefon) {
  if (!telefon || typeof telefon !== 'string') return '';
  const digits = telefon.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.startsWith('40') && digits.length >= 11) return digits;
  if (digits.length === 10 && digits.startsWith('0')) return '4' + digits;
  if (digits.length === 9) return '40' + digits;
  if (digits.length === 10) return '40' + digits;
  return digits.startsWith('40') ? digits : '40' + digits;
}

export default function AgentAmbassadorsList() {
  const [ambassadors, setAmbassadors] = useState([]);
  const [ambassadorTarget, setAmbassadorTarget] = useState(3);
  const [loading, setLoading] = useState(true);

  const handleWhatsAppNudge = (ambassador, target) => {
    const remaining = Math.max(0, target - (ambassador.referralCount ?? 0));
    const phone = formatPhoneForWhatsApp(ambassador.telefon);
    if (!phone) return;
    const nume = ambassador.numeComplet || ambassador.nume || 'acolo';
    const message = `Salut ${nume}! 🎉 Până acum ne-ai recomandat ${ambassador.referralCount ?? 0} prieteni. Mai ai nevoie de doar ${remaining} recomandări pentru a-ți debloca recompensa. Haide să atingem targetul! Nu uita să trimiți link-ul tău mai departe.`;
    window.open(
      'https://wa.me/' + phone + '?text=' + encodeURIComponent(message),
      '_blank',
      'noopener,noreferrer'
    );
  };

  const fetchAmbassadors = async () => {
    setLoading(true);
    try {
      const res = await api.get('/agent/ambassadors');
      setAmbassadors(res.data.ambassadors || []);
      setAmbassadorTarget(res.data.ambassadorTarget ?? 3);
    } catch (err) {
      console.error('Eroare încărcare ambasadori:', err);
      setAmbassadors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAmbassadors();
  }, []);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return <div className="text-[var(--text-muted)]">Încărcare ambasadori...</div>;
  }

  return (
    <div className="agent-leads-table-wrap overflow-x-auto w-full">
      <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
        <div className="card-title">Ambasadorii Mei</div>
        <div className="card-sub">Clienții tăi care recomandă</div>
      </div>
      <table className="agent-leads-table">
        <thead>
          <tr>
            <th>Nume Ambasador</th>
            <th>Email</th>
            <th>Telefon</th>
            <th>Cod Unic</th>
            <th>Recomandări Aduse</th>
            <th>Dată Înscriere</th>
          </tr>
        </thead>
        <tbody>
          {ambassadors.length === 0 ? (
            <tr>
              <td colSpan={6} className="leads-empty">Nu ai încă clienți înrolați ca ambasadori.</td>
            </tr>
          ) : (
            ambassadors.map((a) => (
              <tr key={a.id}>
                <td>
                  <div className="font-medium text-[var(--text)]">{a.numeComplet}</div>
                </td>
                <td className="text-[var(--text-muted)]">{a.email}</td>
                <td className="text-[var(--text-soft)] font-mono text-sm">{a.telefon}</td>
                <td>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/40">
                    {a.ambassadorCode}
                  </span>
                </td>
                <td>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-3 py-1 text-xs font-bold rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                      {a.referralCount} / {ambassadorTarget}
                    </span>
                    {a.referralCount >= ambassadorTarget && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/40" title="Target atins">
                        🏆 Target Atins!
                      </span>
                    )}
                    {a.referralCount > 0 && a.referralCount < ambassadorTarget && a.telefon && (
                      <button
                        type="button"
                        onClick={() => handleWhatsAppNudge(a, ambassadorTarget)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#25D366] text-white hover:bg-[#20bd5a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#25D366] transition-colors"
                        title="Trimite mesaj de impulsionare pe WhatsApp"
                        aria-label="Trimite mesaj de impulsionare pe WhatsApp"
                      >
                        <MessageCircle size={16} aria-hidden />
                      </button>
                    )}
                  </div>
                </td>
                <td className="text-[var(--text-muted)] text-sm">{formatDate(a.dataInscriere)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
